import packageJson from '../../../package.json';
import { EnvVar } from './envVar';

// type defs for runtime config
declare global {
  interface Window {
    env?: {
      AIDPSTUDIO_API_URL?: string;
      AUTH_AUDIENCE?: string;
      AUTH_CLIENT_DOMAIN?: string;
      AUTH_CLIENT_ID?: string;
      AUTH_ORG_ID?: string;
      DD_RUM_APPLICATION_ID?: string;
      DD_RUM_CLIENT_TOKEN?: string;
      DD_RUM_SERVICE?: string;
      DD_RUM_ENV?: string;
      UI_TRACING_DISABLED?: string;
      STUDIO_BASE_PATH?: string;
      DOCS_BASE_URL?: string;
      CONTACT_US_EMAIL?: string;
    };
  }
}

/**
 * Get env vars
 *
 * In prod:
 *   - envsubst replaces ${VAR_NAME} placeholders in config.template.js with actual env var values
 *   - consumed at runtime via window.env
 *
 * In dev (npm run dev):
 *   - script transforms the specified env vars to VITE_ env vars before starting Vite
 *   - Vite injects these as import.meta.env.VITE_* at build time
 *   - window.env contains unresolved template strings (starting with '${'), so we fall back to import.meta.env
 */
const getEnv = ( key: string ): string | undefined => {
  const runtimeValue = window.env?.[key as keyof typeof window.env];
  if ( runtimeValue && !runtimeValue.startsWith( '${' ) ) {
    return runtimeValue;
  }

  // Fallback to build-time env vars (for local dev)
  // These are transformed to VITE_* by transform-env.js
  return import.meta.env[`VITE_${ key }`];
};

/**
 * Environment variables for the application.
 *
 * In prod: reads from window.env (vars populated by envsubst at container startup)
 * In dev: reads from import.meta.env (vars transformed to VITE_ vars by transform-env.js)
 */
class EnvVarsProvider {
  private missingEnvVars: string[] = [];

  AIDPSTUDIO_API_URL: EnvVar = EnvVar.string( 'AIDPSTUDIO_API_URL', getEnv( 'AIDPSTUDIO_API_URL' ) );
  APP_VERSION: EnvVar = EnvVar.string( 'APP_VERSION', packageJson.version );
  AUTH_AUDIENCE: EnvVar = EnvVar.string( 'AUTH_AUDIENCE', getEnv( 'AUTH_AUDIENCE' ) );
  AUTH_CLIENT_DOMAIN: EnvVar = EnvVar.string( 'AUTH_CLIENT_DOMAIN', getEnv( 'AUTH_CLIENT_DOMAIN' ) );
  AUTH_CLIENT_ID: EnvVar = EnvVar.string( 'AUTH_CLIENT_ID', getEnv( 'AUTH_CLIENT_ID' ) );
  AUTH_ORG_ID: EnvVar = EnvVar.string( 'AUTH_ORG_ID', getEnv( 'AUTH_ORG_ID' ) );
  DD_RUM_APPLICATION_ID: EnvVar = EnvVar.string( 'DD_RUM_APPLICATION_ID', getEnv( 'DD_RUM_APPLICATION_ID' ) );
  DD_RUM_CLIENT_TOKEN: EnvVar = EnvVar.string( 'DD_RUM_CLIENT_TOKEN', getEnv( 'DD_RUM_CLIENT_TOKEN' ) );
  DD_RUM_SERVICE: EnvVar = EnvVar.string( 'DD_RUM_SERVICE', getEnv( 'DD_RUM_SERVICE' ) );
  DD_RUM_ENV: EnvVar = EnvVar.string( 'DD_RUM_ENV', getEnv( 'DD_RUM_ENV' ) );
  UI_TRACING_DISABLED: EnvVar<boolean> = EnvVar.boolean( 'UI_TRACING_DISABLED', getEnv( 'UI_TRACING_DISABLED' ) );
  STUDIO_BASE_PATH: EnvVar = EnvVar.string( 'STUDIO_BASE_PATH', getEnv( 'STUDIO_BASE_PATH' ) );
  DOCS_BASE_URL: EnvVar = EnvVar.string( 'DOCS_BASE_URL',getEnv( 'DOCS_BASE_URL' ) );
  CONTACT_US_EMAIL: EnvVar = EnvVar.string( 'CONTACT_US_EMAIL', getEnv( 'CONTACT_US_EMAIL' ) );

  constructor() {
    this.results();
  }

  get<K extends keyof EnvVars>( key: K ): EnvVars[K] {
    const { val } = this[key];
    return val as EnvVars[K];
  }

  private results = () => {
    console.log( this );

    if ( this.missingEnvVars.length ) {
      console.error(
        `-- Missing environment variables: ${ this.missingEnvVars.join( ', ' ) }.`
      );
    } else {
      console.log( '-- All required environment variables are set --' );
    }
  };
}

export const envVarsProvider = new EnvVarsProvider();
export default envVarsProvider;

export type EnvVars = {
  AIDPSTUDIO_API_URL: string;
  APP_VERSION: string;
  AUTH_AUDIENCE: string;
  AUTH_CLIENT_DOMAIN: string;
  AUTH_CLIENT_ID: string;
  AUTH_ORG_ID: string;
  DD_RUM_APPLICATION_ID: string;
  DD_RUM_CLIENT_TOKEN: string;
  DD_RUM_SERVICE: string;
  DD_RUM_ENV: string;
  UI_TRACING_DISABLED: boolean;
  STUDIO_BASE_PATH: string;
  DOCS_BASE_URL: string;
  CONTACT_US_EMAIL: string;
};

export type EnvVarsVal = EnvVars[keyof EnvVars];
